import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { setStorageItem } from "../../utils/sessionStorage";
import { useMerchantStore } from "../../store/merchantStore";
import { Service } from "../../components/interfaces";
import { useUserStore } from "../../store/userStore";
import { API } from "../../utils/axios";
import { MapPin, Star, Zap, ChevronRight } from "lucide-react";
import he from "he";
import toast from "react-hot-toast";

export default function ServiceCardLayout({ item }: { item: Service }) {
  const navigate = useNavigate();
  const updateMerchant = useMerchantStore((state) => state.updateMerchant);
  const [accessEmails, setAccessEmails] = useState([]);
  const [accessEmailApiFetched, setAccessEmailApiFetched] = useState(false);
  const { user } = useUserStore();

  const servicesObject = {
    "1": "Physiotherapy",
    "2": "Chiropractic",
    "3": "Acupuncture",
    "4": "Massage Therapy",
  };

  type ServiceKey = keyof typeof servicesObject;

  const mapServices =
    item?.services && Array.isArray(item.services)
      ? item.services
          .map((key: any) => servicesObject[key as ServiceKey])
          .filter((val: any): val is string => Boolean(val))
      : [];

  const handleSelectService = () => {
    const isAccessed = accessEmails.find(
      (list: any) => user?.email === list?.email,
    );
    if (!isAccessed) return;
    setStorageItem("merchant-data", JSON.stringify(item));
    updateMerchant(item);
    navigate(`/service`);
  };

  useEffect(() => {
    const getAllEmails = async () => {
      try {
        if (accessEmailApiFetched) {
          return;
        }
        const { data } = await API.get(`/access`);
        setAccessEmails(data.emails);
        setAccessEmailApiFetched(true);
      } catch (error) {
        console.log("Error in getAllEmails", error);
        toast.error("Internal servor error")
      }
    };
    getAllEmails();
  }, []);

  return (
    <div
      onClick={handleSelectService}
      className="group flex flex-col h-[480px] w-full cursor-pointer rounded-2xl border border-gray-100 bg-white transition-all duration-300 hover:border-emerald-200 hover:shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
    >
      {/* Image Section */}
      <div className="relative aspect-[16/9] w-full overflow-hidden rounded-t-2xl bg-gray-100">
        {item?.businessPhotos ? (
          <img
            src={item.businessPhotos}
            alt={item?.businessName || "Service"}
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full items-center justify-center text-xs font-medium text-gray-400">
            No Image Available
          </div>
        )}

        {/* Fast Booking Tag */}
        {item?.approval_type === "auto" && (
          <div className="absolute left-3 top-3 flex items-center gap-1.5 rounded-full bg-white/90 px-3 py-1.5 shadow-sm backdrop-blur-md">
            <Zap className="h-3 w-3 fill-emerald-500 text-emerald-500" />
            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-800">
              Instant Book
            </span>
          </div>
        )}
      </div>

      {/* Content Section */}
      <div className="flex flex-1 flex-col p-6">
        {/* Category & Rating */}
        <div className="mb-3 flex items-center justify-between">
          <span className="inline-block rounded-full bg-emerald-50 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-emerald-700">
            {item?.businessType || "Service"}
          </span>
          {/* <div className="flex items-center gap-1">
            <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
            <span className="text-sm font-bold text-gray-900">4.9</span>
          </div> */}
        </div>

        {/* Title */}
        <h3 className="mb-2 line-clamp-1 text-lg font-semibold text-gray-900 transition-colors group-hover:text-emerald-600">
          {item?.businessAs
            ? he.decode(item.businessAs)
            : he.decode(item?.businessName || "No Business Name")}
        </h3>

        {/* Improved Address UX */}
        <div className="mb-4 flex space-x-1">
          <MapPin className="mt-1 h-4 w-4 shrink-0 text-emerald-500" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 text-gray-900">
              <span className="text-sm font-medium">
                {item?.cityData?.city_name}, {item?.stateData?.state_name}
              </span>
            </div>
            <span className="block text-sm text-gray-500 break-words font-normal pt-1">
              {item?.merchantAddress
                ? he.decode(item.merchantAddress)
                : "No address"}
            </span>
          </div>
        </div>

        {/* Description */}
        <p className="mb-5 line-clamp-2 text-sm leading-relaxed text-gray-500">
          {item?.business_desc
            ? he.decode(item.business_desc)
            : "Quality professional services tailored to your specific requirements."}
        </p>

        {/* Services / Tags */}
        <div className="mt-auto flex flex-wrap gap-2">
          {mapServices?.slice(0, 3).map((tag: string, i: number) => (
            <span
              key={i}
              className="rounded-md border border-gray-100 bg-gray-50 px-2 py-0.5 text-[11px] font-medium text-gray-500"
            >
              {tag}
            </span>
          ))}
        </div>
      </div>

      {/* Simple, Stunning CTA */}
      <div className="border-t border-gray-50 px-6 py-4">
        <div className="relative group/button">
          <button
            disabled
            className="w-full rounded-lg bg-emerald-600 py-2.5 text-sm font-semibold text-white opacity-50 cursor-not-allowed"
          >
            View Details & Book
          </button>

          {/* Tooltip */}
          <div className="pointer-events-none absolute left-1/2 -top-9 w-max -translate-x-1/2 rounded-md bg-gray-800 px-3 py-1.5 text-xs text-white opacity-0 transition-opacity group-hover/button:opacity-100">
            Booking unavailable right now
          </div>
        </div>
      </div>
    </div>
  );
}
